<template>
	<div v-if="config" :id="config.section_id" class="banner-promo-content">
		<div class="col-8 banner-promo-text-content">
			<div v-if="showLabelsInPosition1" class="labels-row">
				<div v-if="config.label_1" class="label-item">
					<p>{{ getString(config.label_1) }}</p>
				</div>
				<div v-if="config.label_2" class="label-item">
					<p>{{ getString(config.label_2) }}</p>
				</div>
				<div v-if="config.label_3" class="label-item label-item-3">
					<p>{{ getString(config.label_3) }}</p>
				</div>
			</div>

			<div v-if="config.title || config.subtitle" class="wrapper-title-subtitle">
				<h2 v-if="config.title">{{ getString(config.title) }}</h2>
				<p v-if="config.subtitle">{{ getString(config.subtitle) }}</p>
			</div>

			<div v-if="showDiscountInPosition1" class="discount-code-box">
				<p v-if="config.discount_code_label" class="discount-label">
					{{ getString(config.discount_code_label) }}
				</p>
				<div class="discount-code-content">
					<span class="discount-code-text" v-if="config.discount_code">{{ getString(config.discount_code) }}</span>
					<button class="copy-code-btn" :class="{copied}" @click="copyDiscountCode" aria-label="Copy discount code">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
							<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
						</svg>
					</button>
				</div>
			</div>

			<AppButton v-if="showButtonInPosition1" :id="config.button_id" :text="getString(config.button_label)" @click="handleButtonClick" />

			<div v-if="showLabelsInPosition2" class="labels-row">
				<div v-if="config.label_1" class="label-item">
					<p>{{ getString(config.label_1) }}</p>
				</div>
				<div v-if="config.label_2" class="label-item">
					<p>{{ getString(config.label_2) }}</p>
				</div>
				<div v-if="config.label_3" class="label-item label-item-3">
					<p>{{ getString(config.label_3) }}</p>
				</div>
			</div>
		</div>

		<div class="col-4 banner-promo-image banner-promo-text-content">
			<div v-if="showDiscountInPosition2" class="discount-code-box">
				<p v-if="config.discount_code_label" class="discount-label">
					{{ getString(config.discount_code_label) }}
				</p>
				<div class="discount-code-content">
					<span class="discount-code-text" v-if="config.discount_code">{{ getString(config.discount_code) }}</span>
					<button class="copy-code-btn" :class="{copied}" @click="copyDiscountCode" aria-label="Copy discount code">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
							<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
						</svg>
					</button>
				</div>
			</div>

			<div v-if="config.img" class="wrapper-img" :class="{'desktop-img': config.img_mobile || config.img_webp_mobile}">
				<picture>
					<source v-if="config.img_webp" type="image/webp" :srcset="config.img_webp">
					<img :src="config.img" :alt="getString(config.alt)">
				</picture>
			</div>

			<div v-if="config.img_mobile || config.img_webp_mobile" class="wrapper-img mobile-img">
				<picture>
					<source v-if="config.img_webp_mobile" type="image/webp" :srcset="config.img_webp_mobile">
					<img :src="config.img_mobile" :alt="getString(config.alt)">
				</picture>
			</div>

			<AppButton v-if="showButtonInPosition2" :id="config.button_id" :text="getString(config.button_label)" @click="handleButtonClick" />

			<div v-if="showLabelsInPosition3" class="labels-row">
				<div v-if="config.label_1" class="label-item">
					<p>{{ getString(config.label_1) }}</p>
				</div>
				<div v-if="config.label_2" class="label-item">
					<p>{{ getString(config.label_2) }}</p>
				</div>
				<div v-if="config.label_3" class="label-item label-item-3">
					<p>{{ getString(config.label_3) }}</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {useLanguageStore} from "@/shared/stores";
import AppButton from "@/shared/components/AppButton.vue";
import {useShopStore} from "@/shop/stores";
import {useRouter} from "vue-router";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const shopStore = useShopStore();
		const router = useRouter();
		return {
			getString: languageStore.getString,
			shopStore,
			router
		};
	},
	name: "AppBannerPromo",
	components: {AppButton},
	props: {
		configFile: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			config: null,
			copied: false,
			copiedTimeout: null
		};
	},
	computed: {
		hasLabels() {
			return this.config?.label_1 || this.config?.label_2 || this.config?.label_3;
		},
		hasButton() {
			return this.config?.button_label;
		},
		showLabelsInPosition1() {
			return this.config?.show_only_label === '1' && this.hasLabels;
		},
		showLabelsInPosition2() {
			return this.config?.show_only_label === '2' && this.hasLabels;
		},
		showLabelsInPosition3() {
			return this.config?.show_only_label === '3' && this.hasLabels;
		},
		showDiscountInPosition1() {
			return this.config?.show_only_discount_code === '1' && this.config?.discount_code;
		},
		showDiscountInPosition2() {
			return this.config?.show_only_discount_code === '2' && this.config?.discount_code;
		},
		showButtonInPosition1() {
			return this.config?.show_only_button === '1' && this.hasButton;
		},
		showButtonInPosition2() {
			return this.config?.show_only_button === '2' && this.hasButton;
		}
	},
	methods: {
		async loadConfig() {
			try {
				const module = await import(
					`@/shared/assets/json/promo-banners/${this.configFile}.json`
				);
				this.config = module.default;
			} catch (error) {
				console.error(`Failed to load banner config: ${this.configFile}`, error);
				this.config = null;
			}
		},
		copyDiscountCode() {
			const codeText = this.getString(this.config.discount_code);

			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(codeText).then(() => {
					this.showCopiedState();
				}).catch(err => {
					console.error('Failed to copy:', err);
				});
			} else {
				const textArea = document.createElement('textarea');
				textArea.value = codeText;
				textArea.style.position = 'fixed';
				textArea.style.opacity = '0';
				document.body.appendChild(textArea);
				textArea.focus();
				textArea.select();
				try {
					document.execCommand('copy');
					this.showCopiedState();
				} catch (err) {
					console.error('Fallback copy failed:', err);
				}
				document.body.removeChild(textArea);
			}
		},
		showCopiedState() {
			if (this.copiedTimeout) {
				clearTimeout(this.copiedTimeout);
			}
			this.copied = true;
			this.copiedTimeout = setTimeout(() => {
				this.copied = false;
			}, 2000);
		},
		handleButtonClick() {
			const posItem = this.shopStore.getItemById('qromobundle');

			if (!posItem) {
				console.error('Bundle item not found');
				return;
			}

			this.shopStore.addItemToCart(posItem, 1);

			this.shopStore.autoCheckout = true;

			this.router.push('/plugins/shop');

			setTimeout(() => {
				this.shopStore.showCart = true;
			}, 300);
		}
	},
	mounted() {
		this.loadConfig();
	},
	beforeUnmount() {
		if (this.copiedTimeout) {
			clearTimeout(this.copiedTimeout);
		}
	}
}
</script>

<style scoped lang="scss">
.banner-promo-content {
	display: flex;
	gap: 60px;
	padding: 20px;
	border-radius: 26px;
	width: 100%;
	background: linear-gradient(204deg, hsla(212, 89%, 41%, 0.3) 0%, rgba(11, 97, 198, 0.1) 70%);
	box-shadow: -1px -1px 2px 0px rgba(7, 16, 32, .3) inset, 1px 1px 2px 0px hsla(0, 0%, 100%, .4) inset;

	@media (max-width: 1180px) {
		gap: 40px;
	}

	@media (max-width: 768px) {
		gap: 30px;
		flex-direction: column-reverse;
	}

	.col-8 {
		flex: 0 0 calc(66.6667% - 30px);
		width: calc(66.6667% - 30px);

		@media (max-width: 768px) {
			flex: 0 0 100%;
			width: 100%;
			max-width: 100%;
		}
	}

	.col-4 {
		flex: 0 0 calc(33.3333% - 30px);
		width: calc(33.3333% - 30px);

		@media (max-width: 768px) {
			flex: 0 0 100%;
			width: 100%;
			max-width: 100%;
		}
	}

	.banner-promo-text-content {
		display: flex;
		flex-direction: column;
		gap: 20px;

		@media (max-width: 768px) {
			gap: 15px;
		}

		h2, h3 {
			color: var(--color-txt);
			margin: 0;
		}

		p {
			color: var(--color-txt);
			opacity: 0.6;
			margin: 0;
		}

		.wrapper-title-subtitle {
			display: flex;
			flex-direction: column;
			gap: 5px;
		}
	}

	.discount-code-box {
		display: flex;
		flex-direction: column;
		padding: 10px;
		border: 2px dashed var(--color-txt);
		border-radius: 16px;
		background-color: rgba(7, 82, 163, .2);
		width: fit-content;

		.discount-label {
			font-size: 14px;
			font-weight: 500;
			color: var(--color-txt);
			margin: 0;
			opacity: 0.6;
		}

		.discount-code-content {
			display: flex;
			align-items: center;
			gap: 15px;
		}

		.discount-code-text {
			font-size: 22px;
			font-weight: 700;
			color: var(--color-txt);
			letter-spacing: 1px;

			@media (max-width: 1180px) {
				font-size: 20px;
			}

			@media (max-width: 480px) {
				font-size: 18px;
			}
		}

		.copy-code-btn {
			background: rgba(255, 255, 255, 0.15);
			border: none;
			border-radius: 8px;
			padding: 8px 12px;
			cursor: pointer;
			transition: background-color 0.2s ease;
			display: flex;
			align-items: center;
			justify-content: center;

			&:hover {
				background: rgba(255, 255, 255, 0.25);
			}

			&.copied {
				background: var(--color-green);
			}

			svg {
				width: 20px;
				height: 20px;
				stroke: var(--color-txt);
			}
		}
	}

	.labels-row {
		display: flex;
		gap: 15px;
		flex-wrap: wrap;

		@media (max-width: 1180px) {
			gap: 20px;
		}

		@media (max-width: 768px) {
			gap: 15px;
		}

		@media (max-width: 480px) {
			gap: 10px;
		}

		.label-item {
			border-radius: 20px;
			padding: 2px 10px;
			display: flex;
			align-items: center;
			gap: 5px;
			background-color: rgba(7, 82, 163, .2);
			border: 1px solid rgba(7, 82, 163, .2);

			p {
				font-size: 14px;
				color: var(--color-txt);
				margin: 0;
				opacity: 1;

				@media (max-width: 1180px) {
					font-size: 12px;
				}
			}
		}

		.label-item-3 {
			@media (max-width: 480px) {
				display: none;
			}
		}
	}

	.banner-promo-image {
		display: flex;
		flex-direction: column;
		position: relative;
		align-items: flex-end;
		justify-content: center;
		gap: 25px;

		@media (max-width: 768px) {
			gap: 20px;
		}

		.wrapper-img {
			width: fit-content;
			max-width: 280px;
			height: auto;
			border-radius: 13px;
			overflow: hidden;

			@media (max-width: 768px) {
				max-width: unset;
				max-height: 320px;
			}

			@media (max-width: 480px) {
				max-width: unset;
				max-height: 260px;
			}

			&.desktop-img {
				display: flex;

				@media (max-width: 768px) {
					display: none;
				}
			}

			&.mobile-img {
				display: none;

				@media (max-width: 768px) {
					display: flex;
				}
			}

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
				object-position: center;
				position: relative;
			}
		}
	}

	.base-button {
		min-height: 40px;
		width: 100%;
		:deep(.btn-text) {
			p {
				text-overflow: unset;
				white-space: unset;
			}
		}
	}
}
</style>
